import React from 'react';

const ScorecardInfo = ({ data }) => {
  // Debug the data
  console.log("Scorecard data received:", typeof data, data);
  
  if (!data || !data.scorecard) {
    return (
      <div className="error-message">
        <h3>Stock Scorecard Data Not Available</h3>
        <p>The scorecard data could not be loaded for this stock.</p>
      </div>
    );
  }
  
  const scorecard = data.scorecard;
  console.log("Scorecard object:", scorecard);
  
  // Helper function to extract value from various value formats
  const extractValue = (metricObj) => {
    if (!metricObj) return null;
    
    // If it's already a simple value (number, string, boolean)
    if (typeof metricObj !== 'object' || metricObj === null) {
      return metricObj;
    }
    
    // If it has a direct value property
    if (metricObj.value !== undefined) {
      // Sometimes value itself is an object with more data
      if (typeof metricObj.value === 'object' && metricObj.value !== null) {
        // For the implied growth which has a complex structure
        if (metricObj.value.impliedGrowth !== undefined) {
          return metricObj.value.impliedGrowth;
        }
        
        // Return any key that seems most important
        for (const key of ['value', 'ratio', 'data', 'amount', 'result']) {
          if (metricObj.value[key] !== undefined) {
            return metricObj.value[key];
          }
        }
        
        // If all else fails, return the whole object and we'll handle it in formatting
        return metricObj.value;
      }
      
      // Simple value property
      return metricObj.value;
    }
    
    // Default case - just return as is
    return metricObj;
  };
  
  // Helper function to format value based on its type
  const formatValue = (value) => {
    if (value === null || value === undefined || value === 'null' || value === 'undefined' || value === 'NaN') {
      return 'N/A';
    }
    
    // For boolean values
    if (typeof value === 'boolean') {
      return value ? 'True' : 'False';
    }
    
    // If it's a string that looks like a formatted value already
    if (typeof value === 'string' && (value.startsWith('$') || value.includes('%'))) {
      return value;
    }
    
    // For numeric values
    if (typeof value === 'number' || !isNaN(parseFloat(value))) {
      const num = parseFloat(value);
      
      // For percentages
      if (Math.abs(num) < 0.2 && value.toString().includes('.')) {
        return (num * 100).toFixed(2) + '%';
      }
      
      // For large currency values (like revenue per employee)
      if (num > 1000) {
        return '$' + new Intl.NumberFormat('en-US').format(num);
      }
      
      // For normal numbers (like ratios)
      return num.toFixed(2);
    }
    
    // For string values
    return value.toString();
  };
  
  // Helper function to get CSS class based on value
  const getValueClass = (key, value) => {
    if (value === null || value === undefined || value === 'N/A' || value === 'NaN') return '';
    
    // Handle boolean values
    if (typeof value === 'boolean' || value === 'True' || value === 'False') {
      const boolValue = value === true || value === 'True';
      
      // For these metrics, "True" is positive
      const positiveIfTrue = [
        'steadyDividend',
        'threeYearAvgGrowthBetterThanTenYear',
        'increasingRevGrowthRateFor3Years',
        'fiveYearsOfAssetsGreaterThan1Point5xLiabilities'
      ];
      
      // For these metrics, "False" is positive
      const negativeIfTrue = [
        'fiveYearsIncreasingRevenue'
      ];
      
      if (negativeIfTrue.includes(key)) {
        return boolValue ? 'negative-value' : 'positive-value';
      }
      
      if (positiveIfTrue.includes(key)) {
        return boolValue ? 'positive-value' : 'negative-value';
      }
      
      // Default for other boolean metrics
      return boolValue ? 'positive-value' : 'negative-value';
    }
    
    // Handle numeric values
    let numValue = parseFloat(value);
    if (isNaN(numValue)) return '';
    
    // For these metrics, lower values are better
    if (key === 'marketCapToEnterpriseValueRatio') {
      return numValue < 1 ? 'positive-value' : (numValue > 1.5 ? 'negative-value' : '');
    }
    
    if (key === 'debtToAssetRatio') {
      return numValue < 0.5 ? 'positive-value' : (numValue > 0.7 ? 'negative-value' : '');
    }
    
    // For operatingCashToDividendRatio, higher is better with specific thresholds
    if (key === 'operatingCashToDividendRatio') {
      return numValue >= 4 ? 'positive-value' : (numValue < 1 ? 'negative-value' : '');
    }
    
    // Default for other numeric values
    return numValue > 0 ? 'positive-value' : (numValue < 0 ? 'negative-value' : '');
  };
  
  return (
    <div className="scorecard-info">
      <h2>Proceemo Scorecard</h2>
      
      <div className="scorecard-description">
        <p>
          This scorecard provides key financial metrics and indicators to help assess the stock's
          valuation, financial health, growth trajectory, and overall investment quality.
        </p>
      </div>
      
      <div className="scorecard-table-container">
        <table className="scorecard-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th>Value</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(scorecard).map(([key, info]) => {
              const rawValue = extractValue(info);
              const formattedValue = formatValue(rawValue);
              const category = info.category || key.replace(/([A-Z])/g, ' $1').trim();
              
              return (
                <tr key={key}>
                  <td className="metric-name">{category}</td>
                  <td className={`metric-value ${getValueClass(key, rawValue)}`}>
                    {formattedValue}
                  </td>
                  <td className="metric-description">{info.description || ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      <div className="scorecard-footer">
        <p>
          Note: This scorecard is for informational purposes only and should not be considered investment advice.
          Always conduct your own research before making investment decisions.
        </p>
      </div>
    </div>
  );
};

export default ScorecardInfo;